import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import SidebarDrawer from '../SidebarDrawer';
import { useParams } from 'react-router-dom';
import campaignService from '../../../services/CampaignService';
import { Campaign } from '../../../types/Campaign';

interface OverviewBaseProps {
  renderSidebar: () => React.ReactNode;
  renderContent: (props: {
    campaign: Campaign;
    onSave: (updatedCampaign: Partial<Campaign>) => Promise<Campaign>;
    onPendingChanges: (changes: Partial<Campaign>) => void;
  }) => React.ReactNode;
}

const OverviewBase: React.FC<OverviewBaseProps> = ({ renderSidebar, renderContent }) => {
  const { campaign_id: campaignId, organization_id: organizationId } = useParams<{ campaign_id: string; organization_id: string }>();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [, setPendingChanges] = useState<Partial<Campaign>>({});

  useEffect(() => {
    const fetchCampaign = async () => {
      if (!campaignId || !organizationId) return;

      try {
        const data = await campaignService.getCampaign(organizationId, campaignId);
        setCampaign(data);
      } catch (err) {
        console.error('Failed to fetch campaign data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaign();
  }, [campaignId, organizationId]);

  const handleSave = async (updatedCampaign: Partial<Campaign>) => {
    if (!campaignId || !organizationId || !campaign) return campaign as Campaign;

    try {
      const updated = await campaignService.updateCampaign(
        organizationId,
        campaignId,
        updatedCampaign
      );
      setCampaign(updated);
      return updated;
    } catch (err) {
      console.error('Failed to update campaign:', err);
      throw err;
    }
  };

  const handlePendingChanges = (changes: Partial<Campaign>) => {
    setPendingChanges(changes);
  };

  const sidebarContent = renderSidebar();

  const mainContent = (
    <Box sx={{ width: '100%' }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : campaign ? (
        renderContent({
          campaign,
          onSave: handleSave,
          onPendingChanges: handlePendingChanges
        })
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5">Campaign not found</Typography>
        </Paper>
      )}
    </Box>
  );

  return (
    <SidebarDrawer
      sidebarContent={sidebarContent}
      mainContent={mainContent}
    />
  );
};

export default OverviewBase;
